<script setup lang="ts">
type NavigationKey = 'team' | 'repository' | 'teamspace'

const selectedKey = ref<NavigationKey>('repository')
const isPopoverOpen = ref(false)

const { recentItems } = useRecentNavigation()
const { enabled: areSleuthSkillsEnabled } = useFlag('SLEUTH_SKILLS')
const { name: orgName } = useOrgMetadata()
const { addRecentItem } = useRecentNavigation()

const navLinks = computed(() => {
  const links = [
    {
      label: 'Repository',
      icon: 'light:code',
      key: 'repository',
      click: () => { selectedKey.value = 'repository' },
      active: selectedKey.value === 'repository',
    },
    {
      label: 'Team',
      icon: 'light:users',
      key: 'team',
      click: () => { selectedKey.value = 'team' },
      active: selectedKey.value === 'team',
    },
    {
      label: 'Teamspace',
      icon: 'light:folder',
      key: 'teamspace',
      click: () => { selectedKey.value = 'teamspace' },
      active: selectedKey.value === 'teamspace',
    },
  ]

  if (areSleuthSkillsEnabled.value) {
    links.push({
      label: 'Organization',
      icon: 'light:globe',
      key: 'organization',
      click: handleOrgClick,
      active: false,
    })
  }

  return links
})

const recentLinks = computed(() => recentItems.value.map(item => ({
  label: item.label,
  to: item.to as any,
  click: closePopover,
})))

function closePopover() {
  isPopoverOpen.value = false
}

function handleOrgClick() {
  addRecentItem({
    label: orgName.value || 'Organization',
    to: { name: 'organization', params: {} },
  })
  closePopover()
  navigateTo({ name: 'organization' })
}
</script>

<template>
  <div class="px-2">
    <UPopover
      v-model:open="isPopoverOpen"
      :popper="{ placement: 'bottom-start' }"
      class="w-full"
    >
      <UButton
        variant="ghost"
        color="white-nav"
        size="sm"
        class="mb-px flex flex-1 items-center justify-start px-2"
        label="Navigate to"
        block
        :ui="{ block: 'justify-start' }"
      >
        <template #leading>
          <div>
            <UIcon name="solid:compass" class="-mt-px size-5 text-gray-500" />
          </div>
        </template>
      </UButton>

      <template #panel>
        <div class="flex h-80">
          <div class="min-w-52 bg-gray-50 p-1 dark:bg-gray-950">
            <div class="ml-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400">
              Switch to
            </div>
            <UVerticalNavigation :links="navLinks" />
            <template v-if="recentLinks.length > 0">
              <div class="ml-3 mt-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                Recently visited
              </div>
              <UVerticalNavigation :links="recentLinks" />
            </template>
          </div>
          <div class="w-60 overflow-y-auto p-4">
            <NavigationGoToSearch
              v-if="selectedKey === 'team' || selectedKey === 'repository' || selectedKey === 'teamspace'"
              :object-type="selectedKey"
              @click-link="closePopover"
            />
          </div>
        </div>
      </template>
    </UPopover>
  </div>
</template>
